/**
 * <FileDropzone> — drag-drop + click-to-browse file input primitive.
 *
 * Used by the imports wizard (B2.3a) and reusable for any future
 * upload-from-PC surface (branding logo, gateway-config bundle, …).
 *
 * Pure presentation + event surface; no parsing/storage logic.
 * Callers pass `onFile(file)` and decide what to do.
 *
 * States: idle / dragging-over / disabled. Error states are surfaced
 * by the caller via a separate toast or inline-error rendering.
 */

import { useCallback, useRef, useState, type DragEvent } from "react";
import { File as FileIcon, Upload } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { cn } from "@/lib/ui/cn";

export interface FileDropzoneProps {
  /** Called once a file is dropped or selected (the first one, unless `multiple`). */
  onFile?: (file: File) => void;
  /** With `multiple`: called once with every file dropped or selected. */
  onFiles?: (files: File[]) => void;
  /** Accept several files at once (the browse dialog allows a multi-select). */
  multiple?: boolean;
  /**
   * Comma-separated extension list passed to the hidden input's `accept`.
   * Defaults to common spreadsheet types.
   */
  accept?: string;
  /** Visible accept hint shown in the empty state ("XLSX, CSV"). */
  acceptLabel?: string;
  /** Max file size in MB. Default 50. Caller still validates on parse. */
  maxSizeMb?: number;
  /** Disable interaction (e.g. while a file is being parsed). */
  disabled?: boolean;
  /** Override the icon. */
  icon?: typeof Upload;
  /** Override the body copy. */
  hint?: string;
  className?: string;
}

export function FileDropzone({
  onFile,
  onFiles,
  multiple = false,
  accept = ".xlsx,.xls,.xlsm,.csv,.tsv,.pdf",
  acceptLabel = "XLSX · XLS · CSV · TSV · PDF",
  maxSizeMb = 50,
  disabled = false,
  icon: Icon = Upload,
  hint = "Drop a file here, or click to browse",
  className,
}: FileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      // An oversized file is still passed through: the caller checks it and says so (its own toast).
      if (multiple && onFiles) {
        onFiles(Array.from(files));
      } else {
        onFile?.(files[0]);
      }
      // Reset input so the same file can be re-dropped after error.
      if (inputRef.current) inputRef.current.value = "";
    },
    [onFile, onFiles, multiple],
  );

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  };

  const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
  };

  return (
    <div
      role="button"
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === "Enter" || e.key === " ") && !disabled) {
          e.preventDefault();
          inputRef.current?.click();
        }
      }}
      onDrop={onDrop}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      className={cn(
        // relative: holds the visually hidden file input inside the zone (see RadioCard).
        "relative flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors",
        disabled
          ? "cursor-not-allowed border-border bg-surface-muted/40 opacity-60"
          : dragging
            ? "cursor-copy border-brand bg-brand-muted/30 ring-2 ring-brand/30"
            : "cursor-pointer border-border bg-surface-elevated/40 hover:border-brand/60 hover:bg-brand-muted/10",
        className,
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div
        className={cn(
          "flex size-12 items-center justify-center rounded-full",
          dragging ? "bg-brand text-brand-foreground" : "bg-surface-muted text-on-surface-muted",
        )}
      >
        <Icon className="size-6" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-sm font-medium text-on-surface">{hint}</p>
        <p className="text-[11px] text-on-surface-muted">
          {acceptLabel} · up to {maxSizeMb} MB
        </p>
      </div>
      {!disabled && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          <FileIcon className="mr-1.5 size-3.5" />
          Browse files
        </Button>
      )}
    </div>
  );
}
